import React, { FC, useContext, useEffect } from 'react';
import { AiChatMessage, AiChatMessageType } from "@app/projects/network-assessment/provider-hub/interfaces";
import { QESAlert, QESButton, QESTextArea } from '@app/shared/qes-standards';
import { FlyoutContext } from '@app/shared/Flyouts';
import { FeatureFlagContext, Features } from "@app/feature-flags";

interface AIChatPanelProps {
  sendMessages: (messages: AiChatMessage[]) => Promise<AiChatMessage[]>;
  startMessage: string;
}

const AIChatPanel: FC<AIChatPanelProps> = ({ sendMessages, startMessage }) => {
  const featureFlagContext = useContext(FeatureFlagContext);
  const flyoutContext = useContext(FlyoutContext);
  const [messages, setMessages] = React.useState<AiChatMessage[]>([]);
  const [prompt, setPrompt] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);

  const scrollToBottom = () => {
    const container = document.querySelector('.ai-chat-panel__messages');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages])

  const handleSubmit = async () => {
    setError(false);
    setLoading(true);
    const messagesToSend = [...messages, {
      content: [prompt],
      messageType: AiChatMessageType.UserPrompt,
      threadId: '',
      timestamp: new Date()
    }];
    setMessages(messagesToSend);
    setPrompt('');
    try {
      const resultMessages = await sendMessages(messagesToSend);
      setMessages(resultMessages);
    } catch {
      setError(true);
    }
    setLoading(false);
  }

  const handleClearHistory = () => {
    setError(false);
    setMessages([]);
  }

  const handleClose = () => {
    flyoutContext.handleCloseFlyout().then();
  }

  if (!featureFlagContext.enabledFeatureFlags.has(Features.SNOWFLAKE_AI_POC)) {
    return null;
  }

  return (
    <div className="ai-chat-panel">
      <div className="ai-chat-panel__messages">
        {(!messages || messages.length === 0) && (
          <div className="ai-chat-panel__message">
            <p className="ai-chat-panel__message__response">{startMessage}</p>
          </div>
        )}
        {messages && messages.map((message, index) => (
          <div className="ai-chat-panel__message" key={index}>
            {message.messageType === AiChatMessageType.UserPrompt && (
              <p className="ai-chat-panel__message__prompt">{message.content.join('\n')}</p>
            )}
            {loading && index === messages.length - 1 && (
              <p className="ai-chat-panel__message__response">Responding...</p>
            )}
            {message.messageType === AiChatMessageType.AnalystResponseText && (
              <p className="ai-chat-panel__message__analyst">{message.content.join('\n')}</p>
            )}
            {message.messageType === AiChatMessageType.ResponseText && (
                    <p className="ai-chat-panel__message__response">{message.content.join('\n')}</p>)}
            {message.messageType === AiChatMessageType.AnalystResponseSuggestion && (
              <div className="ai-chat-panel__message__suggestions">
                <p className="ai-chat-panel__message__suggestions-title">Suggestions:</p>
                {message.content.map((suggestion, index) => (
                  <p key={index}>{suggestion}</p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="ai-chat-panel__prompt">
        {error && (
          <QESAlert type="danger" persist={true}>
            <p className="m-0">Something went wrong. Please try again.</p>
          </QESAlert>
        )}
        <QESTextArea
          label=""
          hideLabel={true}
          maxLength={4000}
          rows={4}
          id="ai-chat-panel__prompt"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit().then();
            }
          }}
          testId="ai-chat-panel__prompt"
        />
        <div className="ai-chat-panel__buttons">
          <QESButton
            qesType="secondary-danger"
            qesTestId="ai-chat-panel__close"
            onClick={handleClose}
          >Close</QESButton>
          <div className="ai-chat-panel__right-buttons">
            <p className="m-0">AI can make mistakes, so double-check it.</p>
            <QESButton
              qesType="secondary"
              qesTestId="ai-chat-panel__clear"
              onClick={handleClearHistory}
            >Clear History</QESButton>
            <QESButton
              qesType="primary"
              qesTestId="ai-chat-panel__submit"
              onClick={handleSubmit}
              disabled={!prompt}
            >Submit</QESButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AIChatPanel;
